import { Link } from 'react-router-dom'
import { cn } from '../../../lib/cn'
import { dataSimples } from '../../../lib/datas'
import { reais } from '../../../lib/dinheiro'
import type { ResumoDaOs } from '../../../types/ordemDeServico'
import { SeloDaSituacao } from '../SeloDaSituacao'
import { OS } from '../textosDaOs'
import { COLUNAS, DESCRICAO_DA_LISTA, mesDaOs, periodoDaOs } from './colunasDaOs'

const CELULA = 'px-4 py-3 align-middle text-sm text-slate-700'

/**
 * As ordens em tabela, no computador.
 *
 * Tabela de verdade, com `<th scope="col">`: é o que faz o leitor de tela anunciar "Situação:
 * aguardando entrega ao preposto" ao entrar na célula, em vez de ler uma fileira de textos soltos.
 *
 * A rolagem lateral fica NESTA caixa e nunca na página — tabela larga empurrando o `<body>` faz o
 * menu e o cabeçalho andarem junto, e a tela inteira parece torta.
 *
 * @param props as linhas da página corrente
 * @returns a tabela
 */
export function TabelaDeOs({ itens }: { itens: ResumoDaOs[] }) {
  return (
    <div className="cartao overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <caption className="sr-only">{DESCRICAO_DA_LISTA}</caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {COLUNAS.map((coluna) => (
                <th
                  key={coluna.id}
                  scope="col"
                  className={cn(
                    'cabecalho-de-coluna',
                    coluna.classe,
                  )}
                >
                  {coluna.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itens.map((ordem) => (
              <Linha key={ordem.id} ordem={ordem} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Uma ordem na tabela.
 *
 * A ordem revogada fica em cinza-ardósia com filete na borda, como o histórico importado das GMUDs:
 * ela não é problema nem pendência, é registro que não autoriza mais nada — e some da lista seria
 * pior, porque foi ela que autorizou despesa antes de ser retirada.
 */
function Linha({ ordem }: { ordem: ResumoDaOs }) {
  return (
    <tr
      className={cn(
        'border-b border-slate-100 transition-colors',
        ordem.revogada ? 'bg-slate-100' : 'odd:bg-white even:bg-slate-50/60 hover:bg-niteroi-50',
      )}
    >
      <td className={cn(CELULA, 'border-l-4', ordem.revogada ? 'border-l-slate-400' : 'border-l-transparent')}>
        <Link
          to={`/ordens-servico/${ordem.id}`}
          className="font-semibold tabular-nums text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
        >
          {ordem.numero}
        </Link>
        {/* Os dois números lado a lado, nunca um convertido no outro: contam coisas diferentes, e o
            sistema não corrige o que já foi assinado. */}
        {ordem.numeroExterno && <span className="block text-xs text-slate-500">{ordem.numeroExterno}</span>}
      </td>
      <td className={cn(CELULA, 'hidden lg:table-cell')}>
        {OS.regimes[ordem.regime]}
        {ordem.tipo && <span className="block text-xs text-slate-500">{OS.tipos[ordem.tipo]}</span>}
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap tabular-nums')}>{mesDaOs(ordem)}</td>
      <td className={cn(CELULA, 'hidden whitespace-nowrap tabular-nums md:table-cell')}>{periodoDaOs(ordem)}</td>
      <td className={cn(CELULA, 'hidden whitespace-nowrap tabular-nums lg:table-cell')}>
        <time dateTime={ordem.emitidaEm}>{dataSimples(ordem.emitidaEm)}</time>
      </td>
      <td className={CELULA}>
        <SeloDaSituacao situacao={ordem.situacao} />
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap text-right tabular-nums')}>{reais(ordem.valorTotal)}</td>
    </tr>
  )
}
